import {Heading} from 'cdk-radial';
import ApplicationList from "./ApplicationList";
import { useApplicationStore } from "./ApplicationStore";
import { useHistory } from "react-router-dom";
import { useAppFavorite } from "./useApplicationQueries";
import { Application } from "./ApplicationInterface";
import EmptyStateCard from "../common/EmptyStateCard";
import { useIPRestrictionStore } from "../dealer-admin/ip-access/IPRestrictionStore";
import BannerContainer from "../campaign/BannerContainer";
import OutageNotificationContainer from '../notification/OutageNotificationContainer';
import useAudit from '../../hooks/useAudit';
import launchExternal from '../../util/launchExternal';
import toTestId from "../utils/toTestId";
import { useAnalytics } from '../third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import { useTranslation } from "react-i18next";
import AhaWidget from '../third-party-integration/aha/AhaWidget';
import {AhaPilotWidget} from '../third-party-integration/aha/AhaPilotWidget';
import { useState } from 'react';
import { useUpdateDefaultApplication } from '../startup-app/useUpdateDefaultApplication';
import { useToastStore } from '../components/Toast/useToastStore';
import userProfileStore from '../core/UserProfileStore';
import SetStartupAppDialog from '../startup-app/SetStartupAppDialog';
import { useAdoptEaseStore } from '../adopt-ease/useAdoptEaseStore';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { useUpdateUserAdoptEaseUsage } from '../adopt-ease/useAdoptEase';
import { findAdoptEaseCampaignForApp } from '../adopt-ease/adoptEaseCampaignUtils';
import { useDriveStore } from '../drive/DriveStore';

export function FavoriteAppContainer() {
  const { applications } = useApplicationStore();
  const filteredApps = applications?.filter((app:any) =>  app.isFavorite &&
    (app.id !=='CDKU') && (app.id !=='CCPQA') && (app.id !=='CCP') && (app.id !=='DRC')
  && (app.name !== 'Document Management Platform'));
  return (
    <>
      {filteredApps.length > 0 ? (
        <AppContainer applications={filteredApps}></AppContainer>
      ) : (
        <EmptyStateCard isApplication={true} isUserCard={false} />
      )}
    </>
  );
}

export function ApplicationContainer() {
  const { applications } = useApplicationStore();
  const { t } = useTranslation();
  return (
      <div style={{maxWidth:'1664px', marginBottom:'20px'}}>
      <OutageNotificationContainer/>
      <BannerContainer/>
       <div style={{display: 'flex','justifyContent': 'space-between','flexDirection': 'row' , 'marginBottom':'10px', 'marginTop':'10px'}}>
          <Heading type="subtitle-1" level={6}>
              {t("myUnifyTab.applications")}
          </Heading>
          
        <div style={{display: 'flex', gap: '10px'}}>
          <div style={{display: 'flex'}}>
            <AhaWidget></AhaWidget>
          </div>
          <div style={{display: 'flex'}}>
              <AhaPilotWidget></AhaPilotWidget>
          </div>
        </div>
      </div>
      <AppContainer applications={applications}></AppContainer>
    </div>
  );
}

function AppContainer(props: { applications: Application[] }) {
  const { applications } = props;
  const history = useHistory();
  const { captureEvent} = useAudit();
  const { trackEvent } = useAnalytics();
  const { mutation } = useAppFavorite();
  const ipRestriction = useIPRestrictionStore((state:any) => state.ipRestriction);
  const { t } = useTranslation();

  const { mutate: setDefaultAppMutation } = useUpdateDefaultApplication();
  const { openToast } = useToastStore();
  const { unifyUserProfile, enterpriseId, iamUserProfile, roles, permissions } = userProfileStore();
  const cNumbers = useDriveStore((state) => state.cNumbers);
  const { applications: activeApplications } = useApplicationStore();

  const { mutate: mutateAdoptEaseUpdate } = useUpdateUserAdoptEaseUsage();

  const [startupDialogState, setStartupDialogState] = useState<{
    isOpen: boolean;
    appId: string;
    appName: string;
    isDefault: boolean;
  }>({ isOpen: false, appId: '', appName: '', isDefault: false });
  
  const neverUsedApps = useAdoptEaseStore((state: any) => state.neverUsedApps);
  const adoptEaseCampaigns = useAdoptEaseStore((state) => state.adoptEaseCampaigns);
  const adoptEaseFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_APPLICATION');
  const userStoreIds: string[] = iamUserProfile?.fullUser?.stores?.map((store: any) => store.id) ?? [];
  
  const isAppDefault = (appId: string): boolean => {
    if (!unifyUserProfile?.apps || !Array.isArray(unifyUserProfile.apps)) return false;
    return unifyUserProfile.apps.some(
      (app: any) => app.appId === appId && app.isDefaultApp === true
    );
  };

  const currentDefaultApp = unifyUserProfile?.apps?.find(
    (app: any) =>
      (app.isDefaultApp === true || app.is_default_app === true) &&
      (app.appId || app.app_id) !== startupDialogState.appId
  );

  const currentDefaultWorkflow = unifyUserProfile?.workflows?.find(
    (wf: any) => wf.isDefaultWorkflow === true
  );

  const currentDefaultName = (() => {
    if (currentDefaultApp) {
      const appId = currentDefaultApp.appId || currentDefaultApp.app_id;
      const activeApp = activeApplications?.find((app: any) => app.id === appId);
      return activeApp?.name || '';
    }
    if (currentDefaultWorkflow) {
      const wfId = currentDefaultWorkflow.workflowId;
      for (const app of activeApplications || []) {
        const wf = app.workflows?.find((w: any) => w.workflowId === wfId);
        if (wf) return wf.name;
      }
    }
    return '';
  })();

  const getDialogScenario = (): 'set' | 'remove' | 'update' => {
    if (startupDialogState.isDefault) return 'remove';
    if (currentDefaultApp || currentDefaultWorkflow) return 'update';
    return 'set';
  };

  const onSetDefaultApp = (appId: string, appName: string, isDefault: boolean) => {
    setStartupDialogState({ isOpen: true, appId, appName, isDefault });
  };

  const confirmDefaultApp = () => {
    const settingAsDefault = !startupDialogState.isDefault;
    setDefaultAppMutation(
      { id: startupDialogState.appId, isDefault: settingAsDefault },
      {
        onSuccess: () => {
          trackEvent(AMPLITUDE_EVENTS.SET_STARTUP, {
            'Application': startupDialogState.appName,
            'Action': settingAsDefault ? 'Set' : 'Remove',
          });
          if (settingAsDefault) {
            openToast('startup-toast', 'Success', t('baseCard.startupApp.toastSet', { appName: startupDialogState.appName }));
          } else {
            openToast('startup-toast', 'Success', t('baseCard.startupApp.toastRemove', { appName: startupDialogState.appName }));
          }
        },
        onError: () => {
          openToast('startup-toast', 'Error', t('baseCard.startupApp.toastError'));
        },
      }
    );
    setStartupDialogState({ isOpen: false, appId: '', appName: '', isDefault: false });
  };


  const onCardClick = (item: any) => {
    const isLaunced = launchExternal(item.url);
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
      Application: item.name,
    });
    if(!isLaunced){
        if (item.name === "Unify Administration") {
          history.push("/unify/unify-admin");
        } else {
          history.push(`/unify/aview/${item.id}/N`);
        }
    }
    captureEvent({action:"Opened",type:"Application", name:item.name});
  };

  const onCdkUniversityClick = (id:string,name:string) => {
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
      Application: name,
    });
    history.push(`/unify/aview/${id}/Y`);
  };

  const onOpenInNewTabClick = (appId:string,name:string, url:string) =>{
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const launchType = urlParams.get('launchtype');
    if(launchType === 'tab') {
      const thisEl = document.getElementById(`wf-card-${toTestId(name)}`);
      thisEl?.click();
      return;
    }
    window.open(`${window.location.protocol}//${ window.location.host }/unify/aview/${appId}/N`, '_blank');
  }

  const onFavoriteChange = (id:string,name:string, isFavorite:boolean) => {
    const fav = {id, isFavorite};
    isFavorite && trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
        'Favorite Application': name,
    });

    mutation.mutate(fav);
  };

  const updateAdoptEaseUsage = (name:string) => {
    trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_TRYNOW_SEEN, {
            'Application': name
          });
    const now = (new Date());
    const record: Record<string, Date> = { [name]: now };
    mutateAdoptEaseUpdate(record);
  };

  const checkToShowTryNewAdoptEaseButton = (id: string,name:string) =>{

    if(!adoptEaseFlag){  
      return false;
    }

    const isAppOpened = JSON.parse(localStorage.getItem('opened-apps') || '[]').includes(id);
    const tryNewEnableAdoptEase = !isAppOpened &&
        neverUsedApps.filter((neverUsedApp:any) => neverUsedApp?.appId === id).length > 0 ;
    return tryNewEnableAdoptEase;
  }

  const checkToShowTryNewAdoptEaseCampaign = (id: string) =>
    findAdoptEaseCampaignForApp(adoptEaseCampaigns, id, {
      enterpriseId,
      storeIds: userStoreIds,
      cNumbers,
      roles,
      permissions,
    });

  return (
    <>
     
      <ApplicationList
        applications={applications}
        onClick={onCardClick}
        onCdkUniversityClick={onCdkUniversityClick}
        onOpenInNewTabClick={onOpenInNewTabClick}
        onFavoriteChange={onFavoriteChange}
        restrictions={ipRestriction}
        onSetDefaultApp={onSetDefaultApp}
        isAppDefault={isAppDefault}
        showTryNewAdoptEaseButton={checkToShowTryNewAdoptEaseButton}
        onTryNewAdoptEaseClick={updateAdoptEaseUsage}
        showTryNewAdoptEaseCampaign={checkToShowTryNewAdoptEaseCampaign}
      />

      <SetStartupAppDialog
        isOpen={startupDialogState.isOpen}
        appName={startupDialogState.appName}
        scenario={getDialogScenario()}
        previousDefaultAppName={currentDefaultName}
        onConfirm={confirmDefaultApp}
        onDismiss={() => setStartupDialogState({ isOpen: false, appId: '', appName: '', isDefault: false })}
      />
    </>
  );
}

export default ApplicationContainer;

